import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';

/**
 * Derive 10 theme color variants (lightest 1-2, light 1-2, bright 1-2, dark 1-2, darkest 1-2)
 * from a base accent palette saved during onboarding.
 */
const hexToHsl = (hex) => {
  if (!hex) return [0, 0, 50];
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue, sat, lum = (max + min) / 2;
  if (max === min) { hue = sat = 0; }
  else {
    const d = max - min;
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hue = ((b - r) / d + 2); break;
      case b: hue = ((r - g) / d + 4); break;
      default: hue = 0;
    }
    hue *= 60;
  }
  return [hue, sat * 100, lum * 100];
};

const hslToHex = (h, s, l) => {
  s = s / 100; l = l / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const buildThemeVariants = (basePalette = ['#8B5CF6', '#3B82F6']) => {
  // Convert each palette colour and produce levels.
  const primary = basePalette[0] || '#8B5CF6';
  const [h, s] = hexToHsl(primary);
  return [
    { id: 'lightest-1', label: 'Lightest 1', bg: hslToHex(h, Math.min(s, 30), 97), fg: '#1A1A2E' },
    { id: 'lightest-2', label: 'Lightest 2', bg: hslToHex(h, Math.min(s, 25), 93), fg: '#1A1A2E' },
    { id: 'light-1', label: 'Light 1', bg: hslToHex(h, Math.min(s, 40), 85), fg: '#0E0E23' },
    { id: 'light-2', label: 'Light 2', bg: hslToHex(h, Math.min(s, 35), 75), fg: '#0E0E23' },
    { id: 'bright-1', label: 'Bright 1', bg: primary, fg: '#FFFFFF' },
    { id: 'bright-2', label: 'Bright 2', bg: basePalette[1] || hslToHex(h, s, 55), fg: '#FFFFFF' },
    { id: 'dark-1', label: 'Dark 1', bg: hslToHex(h, s, 30), fg: '#FFFFFF' },
    { id: 'dark-2', label: 'Dark 2', bg: hslToHex(h, s, 22), fg: '#FFFFFF' },
    { id: 'darkest-1', label: 'Darkest 1', bg: hslToHex(h, s, 14), fg: '#FFFFFF' },
    { id: 'darkest-2', label: 'Darkest 2', bg: '#0E0E23', fg: '#FFFFFF' },
  ];
};

/**
 * Floating panel anchored to a section. Three tabs: Design / Background / Colors.
 * "Colors" lists the 10 derived theme variants — clicking applies to that section.
 */
export default function SectionColorPanel({
  block,
  onClose,
  onUpdate,
  palette = ['#8B5CF6', '#3B82F6'],
  sectionContextLabel = 'Editing Section',
}) {
  const [tab, setTab] = useState('colors');
  const variants = useMemo(() => buildThemeVariants(palette), [palette]);
  const style = block?.content?._style || {};
  const currentThemeId = style.themeId || 'darkest-2';

  const applyTheme = (variant) => {
    const newStyle = {
      ...style,
      themeId: variant.id,
      bgColor: variant.bg,
      fontColor: variant.fg,
    };
    onUpdate(block.id, '_style', newStyle);
  };

  return (
    <div className="fixed top-[64px] right-6 z-[150] w-[340px] max-h-[calc(100vh-100px)] bg-[#15152E] border border-white/[0.08] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col" data-testid="section-color-panel">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{sectionContextLabel}</p>
            <p className="text-white font-bold text-[13px] mt-0.5">Section Styles</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-white p-1"><X size={14} /></button>
        </div>
        {/* TABS */}
        <div className="flex items-center gap-5 mt-4 -mb-px">
          {['design', 'background', 'colors', 'animation'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-[12px] font-semibold pb-2 border-b-2 transition-all capitalize ${
                tab === t ? 'text-white border-[#A78BFA]' : 'text-[#9CA3AF] border-transparent hover:text-white'
              }`}
              data-testid={`section-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto px-5 py-4 sqs-scrollbar">
        {tab === 'colors' && (
          <div>
            <p className="text-[11px] text-[#9CA3AF] mb-4 leading-relaxed">
              Select a color theme for this section. Themes are derived from your site palette.
            </p>
            <div className="space-y-2">
              {variants.map((v) => {
                const isActive = currentThemeId === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => applyTheme(v)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition-all ${
                      isActive ? 'border-[#A78BFA] shadow-[0_0_16px_rgba(167,139,250,0.2)]' : 'border-white/[0.06] hover:border-white/[0.16]'
                    }`}
                    style={{ background: v.bg, color: v.fg }}
                    data-testid={`theme-variant-${v.id}`}
                  >
                    <span className="text-[15px] font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Aa</span>
                    <span className="text-[11px] uppercase tracking-[0.18em] font-bold flex-1 text-left">{v.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'background' && (
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Background Color</p>
              <input
                type="color"
                value={style.bgColor || '#0E0E23'}
                onChange={(e) => onUpdate(block.id, '_style', { ...style, bgColor: e.target.value })}
                className="w-full h-10 rounded-lg cursor-pointer bg-transparent border border-white/10"
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Background Image</p>
              {style.bgImage ? (
                <div className="relative rounded-lg overflow-hidden h-24">
                  <img src={style.bgImage} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => onUpdate(block.id, '_style', { ...style, bgImage: '' })}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <input
                  type="url"
                  value={style.bgImage || ''}
                  onChange={(e) => onUpdate(block.id, '_style', { ...style, bgImage: e.target.value })}
                  placeholder="Image URL or upload from Assets"
                  className="w-full bg-white/[0.04] border border-white/[0.06] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                />
              )}
            </div>
          </div>
        )}

        {tab === 'design' && (
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Padding</p>
              <div className="flex gap-2">
                {['small', 'medium', 'large'].map((p) => (
                  <button
                    key={p}
                    onClick={() => onUpdate(block.id, '_style', { ...style, padding: p })}
                    className={`flex-1 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-all ${
                      style.padding === p ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Alignment</p>
              <div className="flex gap-2">
                {['left', 'center', 'right'].map((a) => (
                  <button
                    key={a}
                    onClick={() => onUpdate(block.id, '_style', { ...style, alignment: a })}
                    className={`flex-1 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-all ${
                      style.alignment === a ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {tab === 'animation' && (
          <div className="space-y-4" data-testid="section-anim-panel">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Entrance animation</p>
              <p className="text-[10.5px] text-[#9CA3AF] mb-3 leading-relaxed">Plays once when this section scrolls into view.</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'none',        label: 'None'         },
                  { v: 'fade',        label: 'Fade in'      },
                  { v: 'fade-up',     label: 'Fade ↑'       },
                  { v: 'fade-down',   label: 'Fade ↓'       },
                  { v: 'slide-left',  label: 'Slide ←'      },
                  { v: 'slide-right', label: 'Slide →'      },
                  { v: 'zoom',        label: 'Zoom in'      },
                  { v: 'blur',        label: 'Blur in'      },
                ].map((o) => (
                  <button
                    key={o.v}
                    onClick={() => onUpdate(block.id, '_style', { ...style, animation: o.v })}
                    className={`py-2.5 rounded-md text-[11.5px] font-semibold transition-all ${
                      (style.animation || 'none') === o.v
                        ? 'bg-[#A78BFA] text-[#0E0E23]'
                        : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'
                    }`}
                    data-testid={`section-anim-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Delay</p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: 0,   label: '0' },
                  { v: 150, label: '150ms' },
                  { v: 300, label: '300ms' },
                  { v: 600, label: '600ms' },
                ].map((o) => (
                  <button
                    key={o.v}
                    onClick={() => onUpdate(block.id, '_style', { ...style, animationDelay: o.v })}
                    className={`py-2 rounded-md text-[11px] font-semibold transition-all ${
                      (style.animationDelay || 0) === o.v
                        ? 'bg-[#A78BFA] text-[#0E0E23]'
                        : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'
                    }`}
                    data-testid={`section-anim-delay-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2">Speed</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: 'fast',   label: 'Fast · 400ms' },
                  { v: 'normal', label: 'Normal · 700ms' },
                  { v: 'slow',   label: 'Slow · 1100ms' },
                ].map((o) => (
                  <button
                    key={o.v}
                    onClick={() => onUpdate(block.id, '_style', { ...style, animationSpeed: o.v })}
                    className={`py-2 rounded-md text-[10.5px] font-semibold transition-all ${
                      (style.animationSpeed || 'normal') === o.v
                        ? 'bg-[#A78BFA] text-[#0E0E23]'
                        : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'
                    }`}
                    data-testid={`section-anim-speed-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
        <button className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold hover:text-white">Theme Editor</button>
        <button onClick={onClose} className="text-[10px] uppercase tracking-[0.18em] text-white font-bold hover:text-[#A78BFA]" data-testid="section-panel-done">Done</button>
      </div>
    </div>
  );
}

